import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsService, DocumentPermission, SetPermissionInput } from '@/services/api/permissions';
import { departmentsService } from '@/services/api/departments';
import { usersService } from '@/services/api/users';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Globe,
  Building2,
  User,
  Loader2,
  Trash2,
  Plus,
  Eye,
  Search,
  Pencil,
  Trash,
  Download,
  Shield,
} from 'lucide-react';
import { Document } from '@/services/api/documents';

interface DocumentPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: Document | null;
}

type PermissionFlag = 'canView' | 'canSearch' | 'canEdit' | 'canDelete' | 'canDownload';

const PERMISSION_FLAGS: { key: PermissionFlag; label: string; icon: React.ElementType }[] = [
  { key: 'canView', label: 'View', icon: Eye },
  { key: 'canSearch', label: 'Search/Chat', icon: Search },
  { key: 'canEdit', label: 'Edit', icon: Pencil },
  { key: 'canDelete', label: 'Delete', icon: Trash },
  { key: 'canDownload', label: 'Download', icon: Download },
];

export function DocumentPermissionsDialog({
  open,
  onOpenChange,
  document,
}: DocumentPermissionsDialogProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [newPermissionType, setNewPermissionType] = useState<'department' | 'user'>('department');
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [newPermissions, setNewPermissions] = useState<Record<PermissionFlag, boolean>>({
    canView: true,
    canSearch: true,
    canEdit: false,
    canDelete: false,
    canDownload: true,
  });

  // Fetch current permissions
  const { data: permissionsData, isLoading: loadingPerms } = useQuery({
    queryKey: ['document-permissions', document?.id],
    queryFn: () => permissionsService.getDocumentPermissions(document!.id),
    enabled: !!document?.id && open,
  });

  // Fetch departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.listDepartments(),
    enabled: open,
  });

  // Fetch users
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.listUsers({ limit: 100 }),
    enabled: open,
  });

  // Set permission mutation
  const setPermissionMutation = useMutation({
    mutationFn: (input: SetPermissionInput) =>
      permissionsService.setPermission(document!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-permissions', document?.id] });
      setSelectedTargetId('');
      setNewPermissions({
        canView: true,
        canSearch: true,
        canEdit: false,
        canDelete: false,
        canDownload: true,
      });
    },
  });

  // Remove permission mutation
  const removePermissionMutation = useMutation({
    mutationFn: (permissionId: string) =>
      permissionsService.removePermission(document!.id, permissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-permissions', document?.id] });
    },
  });

  // Set public access mutation
  const setPublicMutation = useMutation({
    mutationFn: (permissions: Record<string, boolean>) =>
      permissionsService.setPublicAccess(document!.id, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-permissions', document?.id] });
    },
  });

  // Remove public access mutation
  const removePublicMutation = useMutation({
    mutationFn: () => permissionsService.removePublicAccess(document!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-permissions', document?.id] });
    },
  });

  const publicPermission = permissionsData?.permissions.find((p) => p.permissionType === 'public');
  const departmentPermissions = permissionsData?.permissions.filter(
    (p) => p.permissionType === 'department'
  ) ?? [];
  const userPermissions = permissionsData?.permissions.filter(
    (p) => p.permissionType === 'user'
  ) ?? [];

  // Filter out already assigned departments/users
  const availableDepartments = departmentsData?.departments.filter(
    (d) => !departmentPermissions.find((p) => p.departmentId === d.id)
  ) ?? [];
  const availableUsers = usersData?.users.filter(
    (u) => !userPermissions.find((p) => p.userId === u.id)
  ) ?? [];

  const handleAddPermission = () => {
    const input: SetPermissionInput = {
      permissionType: newPermissionType,
      ...(newPermissionType === 'department' && { departmentId: selectedTargetId }),
      ...(newPermissionType === 'user' && { userId: selectedTargetId }),
      ...newPermissions,
    };
    setPermissionMutation.mutate(input);
  };

  const handleTogglePublic = () => {
    if (publicPermission) {
      removePublicMutation.mutate();
    } else {
      setPublicMutation.mutate({
        canView: true,
        canSearch: true,
        canEdit: false,
        canDelete: false,
        canDownload: false,
      });
    }
  };

  const handleUpdatePublicPermission = (flag: PermissionFlag, value: boolean) => {
    if (publicPermission) {
      setPublicMutation.mutate({
        [flag]: value,
      });
    }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Document Permissions
          </DialogTitle>
          <DialogDescription className="truncate">
            Manage who can access "{document.originalFilename}"
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">
              <Globe className="mr-2 h-4 w-4" />
              Public
            </TabsTrigger>
            <TabsTrigger value="departments">
              <Building2 className="mr-2 h-4 w-4" />
              Departments ({departmentPermissions.length})
            </TabsTrigger>
            <TabsTrigger value="users">
              <User className="mr-2 h-4 w-4" />
              Users ({userPermissions.length})
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            {/* Public Access Tab */}
            <TabsContent value="overview" className="m-0 space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Globe className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Public Access</p>
                    <p className="text-sm text-muted-foreground">
                      Allow all users in your organization to access this document
                    </p>
                  </div>
                </div>
                <Button
                  variant={publicPermission ? 'destructive' : 'default'}
                  size="sm"
                  onClick={handleTogglePublic}
                  disabled={setPublicMutation.isPending || removePublicMutation.isPending}
                >
                  {(setPublicMutation.isPending || removePublicMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {publicPermission ? 'Disable' : 'Enable'}
                </Button>
              </div>

              {publicPermission && (
                <div className="p-4 border rounded-lg space-y-3">
                  <p className="font-medium text-sm">Public Permissions</p>
                  <div className="grid grid-cols-2 gap-3">
                    {PERMISSION_FLAGS.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`public-${key}`}
                          checked={publicPermission[key]}
                          onCheckedChange={(checked) =>
                            handleUpdatePublicPermission(key, checked as boolean)
                          }
                        />
                        <label
                          htmlFor={`public-${key}`}
                          className="text-sm font-medium leading-none flex items-center gap-1"
                        >
                          <Icon className="h-3 w-3" />
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Departments Tab */}
            <TabsContent value="departments" className="m-0 space-y-4">
              {/* Add Department */}
              <div className="p-4 border rounded-lg space-y-3">
                <p className="font-medium text-sm">Add Department Access</p>
                <div className="flex gap-2">
                  <Select value={selectedTargetId} onValueChange={setSelectedTargetId}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select department..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDepartments.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          All departments have access
                        </div>
                      ) : (
                        availableDepartments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => {
                      setNewPermissionType('department');
                      handleAddPermission();
                    }}
                    disabled={!selectedTargetId || setPermissionMutation.isPending}
                  >
                    {setPermissionMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {PERMISSION_FLAGS.map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-1">
                      <Checkbox
                        id={`new-dept-${key}`}
                        checked={newPermissions[key]}
                        onCheckedChange={(checked) =>
                          setNewPermissions((prev) => ({ ...prev, [key]: checked as boolean }))
                        }
                      />
                      <label htmlFor={`new-dept-${key}`} className="text-xs">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Department List */}
              {loadingPerms ? (
                <Skeleton className="h-32 w-full" />
              ) : departmentPermissions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="mx-auto h-8 w-8 mb-2" />
                  <p>No department-specific access configured</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Department</TableHead>
                        <TableHead className="text-center">View</TableHead>
                        <TableHead className="text-center">Search</TableHead>
                        <TableHead className="text-center">Edit</TableHead>
                        <TableHead className="text-center">Delete</TableHead>
                        <TableHead className="text-center">Download</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {departmentPermissions.map((perm) => (
                        <TableRow key={perm.id}>
                          <TableCell className="font-medium">{perm.departmentName}</TableCell>
                          <TableCell className="text-center">
                            {perm.canView && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canSearch && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canEdit && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canDelete && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canDownload && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removePermissionMutation.mutate(perm.id)}
                              disabled={removePermissionMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="m-0 space-y-4">
              {/* Add User */}
              <div className="p-4 border rounded-lg space-y-3">
                <p className="font-medium text-sm">Add User Access</p>
                <div className="flex gap-2">
                  <Select
                    value={selectedTargetId}
                    onValueChange={setSelectedTargetId}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUsers.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          All users have access
                        </div>
                      ) : (
                        availableUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.firstName} {user.lastName} ({user.email})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => {
                      setNewPermissionType('user');
                      handleAddPermission();
                    }}
                    disabled={!selectedTargetId || setPermissionMutation.isPending}
                  >
                    {setPermissionMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {PERMISSION_FLAGS.map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-1">
                      <Checkbox
                        id={`new-user-${key}`}
                        checked={newPermissions[key]}
                        onCheckedChange={(checked) =>
                          setNewPermissions((prev) => ({ ...prev, [key]: checked as boolean }))
                        }
                      />
                      <label htmlFor={`new-user-${key}`} className="text-xs">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* User List */}
              {loadingPerms ? (
                <Skeleton className="h-32 w-full" />
              ) : userPermissions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="mx-auto h-8 w-8 mb-2" />
                  <p>No user-specific access configured</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead className="text-center">View</TableHead>
                        <TableHead className="text-center">Search</TableHead>
                        <TableHead className="text-center">Edit</TableHead>
                        <TableHead className="text-center">Delete</TableHead>
                        <TableHead className="text-center">Download</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userPermissions.map((perm) => (
                        <TableRow key={perm.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{perm.userName}</p>
                              <p className="text-xs text-muted-foreground">{perm.userEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canView && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canSearch && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canEdit && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canDelete && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {perm.canDownload && <Badge variant="outline">Yes</Badge>}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removePermissionMutation.mutate(perm.id)}
                              disabled={removePermissionMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
